<template>
  <!-- Llamado a la compra: va inmediatamente debajo del hero porque es la ruta
       más corta del visitante hacia un producto ya fabricado. Si el catálogo no
       carga, la banda sigue siendo un CTA válido (sólo pierde las miniaturas). -->
  <section class="catalog-teaser section" aria-labelledby="catalog-teaser-title">
    <div class="teaser-head">
      <p class="eyebrow">Catálogo 3D</p>
      <h2 id="catalog-teaser-title" class="teaser-title">
        Piezas listas para pedir hoy
      </h2>
      <p class="teaser-text">
        Maceteros, repuestos, figuras y organizadores que ya fabricamos. Elige
        una, la adaptamos en tamaño, material y color, y te la entregamos.
      </p>
      <div class="teaser-actions">
        <router-link to="/print3d/catalogo" class="teaser-cta">
          <span>Ver catálogo completo</span>
          <ArrowRight :size="20" :stroke-width="3" />
        </router-link>
        <router-link to="/print3d" class="teaser-link">
          O cotiza tu propia pieza
        </router-link>
      </div>
    </div>

    <ul v-if="products.length" class="teaser-grid">
      <li v-for="product in products" :key="product.id">
        <router-link
          class="teaser-card"
          :to="{ path: '/print3d/catalogo', query: { q: product.name } }"
        >
          <span class="card-media">
            <img
              v-if="product.imageUrl"
              :src="product.imageUrl"
              :alt="product.name"
              loading="lazy"
              decoding="async"
            />
          </span>
          <span class="card-name">{{ product.name }}</span>
          <span class="card-price">Desde {{ formatPrice(product) }}</span>
        </router-link>
      </li>
    </ul>
  </section>
</template>

<script>
import { ArrowRight } from 'lucide-vue-next';
import { getProducts } from '@/services/print3dService';
import { formatCurrencyRAE } from '@/utils/numberFormat';

// Cuántas piezas se asoman en la home. Cuatro llenan la fila en escritorio sin
// competir con el hero ni alargar la página.
const MAX_TEASER = 4;

export default {
  name: 'CatalogTeaser',
  components: { ArrowRight },
  data() {
    return { products: [] };
  },
  async mounted() {
    // Los destacados de temporada son los mismos que encabezan /print3d: la
    // home no elige aparte qué mostrar.
    const { products } = await getProducts({ featured: true });
    this.products = products.slice(0, MAX_TEASER);
  },
  methods: {
    formatPrice(product) {
      return formatCurrencyRAE(product.priceFrom || 0, product.currency || 'COP');
    },
  },
};
</script>

<style scoped>
.catalog-teaser {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(0, 1.25fr);
  gap: var(--space-md);
  align-items: center;
  padding: var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(46, 181, 190, 0.10), rgba(82, 109, 130, 0.06));
}

.eyebrow {
  font-family: var(--font-body);
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-brand);
  margin: 0 0 0.4rem;
}

.teaser-title {
  font-family: var(--font-subtitle);
  font-weight: 700;
  font-size: clamp(1.6rem, 2.6vw, 2.25rem);
  line-height: 1.15;
  color: var(--color-title);
  margin: 0 0 0.6rem;
}

.teaser-text {
  font-family: var(--font-body);
  color: var(--color-text);
  margin: 0 0 var(--space-sm);
  max-width: 46ch;
}

.teaser-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.25rem;
}

.teaser-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.4rem;
  border-radius: 9999px;
  background: var(--color-action);
  color: var(--color-white);
  font-family: var(--font-body);
  font-size: 1.05rem;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease;
}

.teaser-cta:hover {
  background: var(--color-action-hover);
  transform: translateY(-2px);
}

.teaser-cta:focus-visible,
.teaser-link:focus-visible,
.teaser-card:focus-visible {
  outline: 2px solid var(--color-action);
  outline-offset: 3px;
}

.teaser-link {
  font-family: var(--font-body);
  color: var(--color-action);
  text-decoration: underline;
}

.teaser-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.teaser-card {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  height: 100%;
  padding: 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: 14px;
  background: var(--color-white);
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.teaser-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 18px rgba(11, 19, 42, 0.12);
}

.card-media {
  display: block;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  background: var(--color-surface);
  overflow: hidden;
}

.card-media img {
  width: 100%;
  height: 100%;
  /* `contain`, igual que la ficha del catálogo: la pieza no se recorta. */
  object-fit: contain;
  display: block;
}

.card-name {
  font-family: var(--font-subtitle);
  font-size: 0.9rem;
  color: var(--color-text-strong);
  line-height: 1.2;
}

.card-price {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--color-action);
}

@media (max-width: 1000px) {
  .catalog-teaser {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 550px) {
  .teaser-grid {
    /* Dos columnas antes que miniaturas ilegibles de 70px. */
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
